/**
 * Cross-platform authentication provider
 *
 * NOTE: legacy filename — there is NO WAM broker here (see below), and the
 * interactive branch is unreachable at runtime (the MCP server constructs
 * this provider silent-only). All interactive sign-in is device code by
 * decision — see the DECISION note in device-code.ts before changing that.
 *
 * Uses MSAL with browser-based interactive auth for sign-in.
 * Tokens are cached using platform-appropriate secure storage:
 * - Windows: DPAPI
 * - macOS: Keychain
 * - Linux: libsecret or plaintext fallback
 */

import {
  PublicClientApplication,
  LogLevel,
  type Configuration,
  type AccountInfo,
  type AuthenticationResult,
} from "@azure/msal-node";
import { execFile } from "child_process";
import { createHash } from "node:crypto";
import type { AuthProvider, WamAuthConfig } from "./types.js";
import { RESOURCES, COPILOT_STUDIO_RESOURCE } from "./types.js";
import { CopilotStudioManagementAuthError } from "./copilot-studio-auth-error.js";
import {
  validatePowerPlatformClaims,
  powerPlatformClaimsFailureMessage,
} from "./power-platform-claims.js";
import { validateClaimsBinding } from "./claims-challenge-store.js";
import {
  AuthenticationError,
  silentOnlyAuthError,
  isInteractionRequiredError,
} from "../utils/errors.js";
import { DEFAULT_AUTHORITY, SCOPES, isAllowedTokenDomain } from "../utils/constants.js";
import { validateTenantId, validateHttpsUrl, tenantAuthorityFor, GUID_PATTERN } from "../utils/security.js";
import { createPersistentCachePlugin, permitEmptyTokenCacheWrite } from "./token-cache.js";
import { saveAccount, loadAccount, clearAccountCache } from "./account-cache.js";
import { logger } from "../utils/logger.js";

/**
 * Open URL in the system's default browser
 * Uses execFile instead of exec to prevent command injection
 */
export async function openBrowser(url: string): Promise<void> {
  // Validate URL to prevent injection
  try {
    const parsed = new URL(url);
    if (!["http:", "https:"].includes(parsed.protocol)) {
      throw new Error(`Invalid URL protocol: ${parsed.protocol}`);
    }
  } catch (err) {
    throw new Error(
      `Invalid URL for browser: ${err instanceof Error ? err.message : String(err)}`,
      {
        cause: err,
      },
    );
  }

  // Platform-specific browser opener
  const opener =
    process.platform === "win32"
      ? { cmd: "cmd.exe", args: ["/c", "start", "", url] }
      : process.platform === "darwin"
        ? { cmd: "open", args: [url] }
        : { cmd: "xdg-open", args: [url] };

  return new Promise((resolve, reject) => {
    // execFile doesn't invoke shell, preventing command injection
    execFile(opener.cmd, opener.args, { timeout: 10000 }, (error) => {
      if (error) {
        reject(error instanceof Error ? error : new Error("Failed to open browser"));
      } else {
        resolve();
      }
    });
  });
}

/**
 * Create an authentication provider using browser-based auth
 * @param silentOnly - If true, only attempt silent auth (for MCP server mode)
 */
export async function createWamAuthProvider(
  config: WamAuthConfig,
  silentOnly = false,
): Promise<AuthProvider> {
  const { clientId, tenantId, cachePath } = config;

  // Build authority URL - use "organizations" for multi-tenant work accounts
  // Validate tenant ID to prevent authority URL manipulation
  const validatedTenantId = tenantId ? validateTenantId(tenantId) : null;
  const authority = validatedTenantId
    ? `https://login.microsoftonline.com/${validatedTenantId}`
    : DEFAULT_AUTHORITY;

  // Create persistent cache plugin for token storage
  const cachePlugin = await createPersistentCachePlugin(cachePath);
  // MSAL can include a claims challenge in even non-PII error messages. Keep
  // suppression active until ALL overlapping sensitive acquisitions have settled.
  let claimsAcquisitionsInFlight = 0;

  // MSAL configuration - use file cache only, no WAM broker
  // WAM broker tokens don't persist across processes, so we use browser popup
  // for interactive auth which properly writes tokens to the file cache
  const msalConfig: Configuration = {
    auth: {
      clientId,
      authority,
    },
    cache: {
      cachePlugin,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          if (containsPii || claimsAcquisitionsInFlight > 0) return; // Never log claims or PII
          // Only log Error, Warning, Info from MSAL — suppress Verbose/Trace
          // to prevent leaking internal MSAL details (correlation IDs, token
          // metadata, cache operations) even when LOG_LEVEL=trace
          switch (level) {
            case LogLevel.Error:
              logger.error({ source: "msal" }, message);
              break;
            case LogLevel.Warning:
              logger.warn({ source: "msal" }, message);
              break;
            case LogLevel.Info:
              logger.debug({ source: "msal" }, message);
              break;
            default:
              // Verbose/Trace intentionally suppressed
              break;
          }
        },
        piiLoggingEnabled: false,
      },
    },
  };

  const pca = new PublicClientApplication(msalConfig);
  let currentAccount: AccountInfo | null = null;
  const pendingTokenRequests = new Map<string, Promise<AuthenticationResult>>();
  const pendingClaimsRequests = new Map<string, Promise<string>>();

  // Honor the selected account just like device-code and explicit sign_in.
  // MSAL enumeration order is not an account selection: choosing accounts[0]
  // first could refresh account B in sign_in but execute tools as account A.
  currentAccount = loadAccount();
  if (!currentAccount) {
    try {
      const accounts = await pca.getAllAccounts();
      logger.debug({ accountCount: accounts.length }, "Accounts from MSAL cache");
      currentAccount = accounts[0] ?? null;
    } catch (err) {
      logger.debug({ err }, "Failed to get accounts from MSAL cache");
    }
  }

  /**
   * Acquire token via WAM native broker.
   * Uses a mutex to prevent concurrent token refresh races.
   */
  async function acquireToken(scopes: readonly string[]): Promise<AuthenticationResult> {
    // Key mutex per sorted scope set to prevent wrong-token returns
    const key = [...scopes].sort().join("|");
    const pending = pendingTokenRequests.get(key);
    if (pending) {
      return pending;
    }
    const promise = acquireTokenInternal(scopes);
    pendingTokenRequests.set(key, promise);
    try {
      return await promise;
    } finally {
      pendingTokenRequests.delete(key);
    }
  }

  async function acquireTokenInternal(scopes: readonly string[]): Promise<AuthenticationResult> {
    const mutableScopes = [...scopes];
    let silentError: unknown;

    // Try silent acquisition first
    if (currentAccount) {
      try {
        logger.debug(
          { username: currentAccount.username, scopes: mutableScopes },
          "Attempting silent token acquisition",
        );
        // Pin the authority to the account's home tenant — silent redemption
        // against /common fails for some accounts (see device-code.ts).
        const result = await pca.acquireTokenSilent({
          scopes: mutableScopes,
          account: currentAccount,
          authority: tenantAuthorityFor(currentAccount.tenantId, authority),
        });
        logger.debug("Silent token acquisition succeeded");
        return result;
      } catch (err) {
        logger.debug(
          { err: err instanceof Error ? err.message : String(err) },
          "Silent token acquisition failed",
        );
        silentError = err;
        // Silent failed, will try interactive if allowed
      }
    } else {
      logger.debug("No account available for silent auth");
    }

    // In silent-only mode (MCP server), fail fast instead of hanging —
    // surfacing consent errors (AADSTS65001) distinctly so users aren't told
    // to re-run --setup for a problem that --setup can't fix.
    if (silentOnly) {
      throw silentOnlyAuthError(silentError, mutableScopes);
    }

    // If we already have an account, this is a secondary resource probe.
    // A failure explicitly demanding interaction (consent/MFA/policy) can
    // only be satisfied interactively — fall through to the broker popup.
    // Anything else fails with the REAL error instead of the old hardcoded
    // "resource may not be registered" message.
    if (currentAccount) {
      if (!isInteractionRequiredError(silentError)) {
        const detail =
          silentError instanceof Error
            ? silentError.message
            : String(silentError ?? "unknown error");
        throw new AuthenticationError(
          `Silent token acquisition failed for scopes ${mutableScopes.join(", ")}: ${detail}`,
        );
      }
      logger.info(
        { scopes: mutableScopes },
        "Silent acquisition requires interaction — starting interactive auth for this resource",
      );
    }

    // Interactive authentication via browser popup
    try {
      const result = await pca.acquireTokenInteractive({
        scopes: mutableScopes,
        openBrowser,
        successTemplate:
          "<html><body><h1>Authentication successful!</h1><p>You can close this window.</p></body></html>",
        errorTemplate:
          "<html><body><h1>Authentication failed</h1><p>Error: {{error}}</p></body></html>",
      });

      if (!result) {
        throw new AuthenticationError("WAM authentication returned null");
      }

      if (result.account) {
        currentAccount = result.account;
        // Persist account for use by MCP server process
        saveAccount(result.account);
      }

      return result;
    } catch (err) {
      if (err instanceof AuthenticationError) {
        throw err;
      }
      throw new AuthenticationError(
        `Failed to authenticate: ${err instanceof Error ? err.message : String(err)}`,
        err,
      );
    }
  }

  const provider: AuthProvider = {
    async getPowerPlatformTokenSilentForCurrentAccount(expectedTenantId: string): Promise<string> {
      const unavailable = () =>
        new AuthenticationError(
          "Selected-account Power Platform authentication unavailable; no fallback or sign-in was attempted.",
        );
      const account = currentAccount;
      if (
        !account ||
        typeof expectedTenantId !== "string" ||
        expectedTenantId.length !== 36 ||
        !GUID_PATTERN.test(expectedTenantId) ||
        typeof account.tenantId !== "string" ||
        account.tenantId.length !== 36 ||
        !GUID_PATTERN.test(account.tenantId) ||
        account.tenantId.toLowerCase() !== expectedTenantId.toLowerCase()
      ) {
        throw unavailable();
      }
      const snapshot = { ...account };
      const identityKeys = [
        "homeAccountId",
        "localAccountId",
        "tenantId",
        "environment",
        "username",
      ] as const;
      if (
        identityKeys.some((key) => typeof snapshot[key] !== "string" || !snapshot[key].trim()) ||
        snapshot.localAccountId.length !== 36 ||
        !GUID_PATTERN.test(snapshot.localAccountId)
      ) {
        throw unavailable();
      }
      claimsAcquisitionsInFlight++;
      try {
        // Fixed audience and tenant, no claims, forceRefresh or interactive fallback.
        const result = await pca.acquireTokenSilent({
          account,
          scopes: [`${RESOURCES.POWER_PLATFORM_API}/.default`],
          authority: tenantAuthorityFor(snapshot.tenantId, authority),
        });
        if (
          currentAccount !== account ||
          identityKeys.some(
            (key) => result.account?.[key] !== snapshot[key] || currentAccount?.[key] !== snapshot[key],
          ) ||
          typeof result.accessToken !== "string" ||
          !result.accessToken.trim() ||
          /\s/.test(result.accessToken)
        ) {
          throw unavailable();
        }
        return result.accessToken;
      } catch {
        // Do not attach SDK errors, challenges, claims, account values or tokens.
        throw unavailable();
      } finally {
        claimsAcquisitionsInFlight--;
      }
    },

    async getGraphTokenSilentForCurrentAccount(
      expectedTenantId: string,
      options?: { forceRefresh?: boolean },
    ): Promise<string> {
      const unavailable = () => new AuthenticationError("Selected-account Graph authentication unavailable; no fallback or sign-in was attempted.");
      const account = currentAccount;
      if (!account || typeof expectedTenantId !== "string" || !GUID_PATTERN.test(expectedTenantId) ||
        typeof account.tenantId !== "string" || !GUID_PATTERN.test(account.tenantId) ||
        account.tenantId.toLowerCase() !== expectedTenantId.toLowerCase()) throw unavailable();
      const snapshot = { ...account };
      const identityKeys = ["homeAccountId", "localAccountId", "tenantId", "environment"] as const;
      if (identityKeys.some((key) => typeof snapshot[key] !== "string" || !snapshot[key])) throw unavailable();
      claimsAcquisitionsInFlight++;
      try {
        const result = await pca.acquireTokenSilent({
          account, scopes: [`${RESOURCES.GRAPH}/.default`],
          authority: tenantAuthorityFor(snapshot.tenantId, authority),
          ...(options?.forceRefresh === true ? { forceRefresh: true } : {}),
        });
        if (currentAccount !== account ||
          identityKeys.some((key) => result.account?.[key] !== snapshot[key] || currentAccount?.[key] !== snapshot[key]) ||
          typeof result.accessToken !== "string" || !result.accessToken.trim()) throw unavailable();
        return result.accessToken;
      } catch { throw unavailable(); }
      finally { claimsAcquisitionsInFlight--; }
    },

    async getCopilotStudioTokenSilent(): Promise<string> {
      const account = currentAccount;
      const unavailable = (error?: unknown, mismatch = false) => new CopilotStudioManagementAuthError(error, mismatch);
      if (!account) throw unavailable();
      const identityKeys = ["homeAccountId", "localAccountId", "tenantId", "environment"] as const;
      const selectedIdentity = { ...account };
      // Suppress SDK messages too: even non-PII authentication errors can carry claims.
      claimsAcquisitionsInFlight++;
      try {
        const result = await pca.acquireTokenSilent({
          account,
          scopes: [`${COPILOT_STUDIO_RESOURCE}/.default`],
          authority: tenantAuthorityFor(account.tenantId, authority),
        });
        if (
          typeof result?.accessToken !== "string" || !result.accessToken.trim()
        ) throw unavailable();
        if (currentAccount !== account ||
          identityKeys.some((key) => result.account?.[key] !== selectedIdentity[key] || currentAccount?.[key] !== selectedIdentity[key])
        ) throw unavailable(undefined, true);
        return result.accessToken;
      } catch (error) {
        throw unavailable(error);
      } finally {
        claimsAcquisitionsInFlight--;
      }
    },

    getPowerPlatformClaimsBinding() {
      if (!currentAccount) return null;
      const binding = {
        clientId,
        resource: RESOURCES.POWER_PLATFORM_API,
        account: {
          homeAccountId: currentAccount.homeAccountId,
          localAccountId: currentAccount.localAccountId,
          tenantId: currentAccount.tenantId,
          environment: currentAccount.environment,
          username: currentAccount.username,
        },
      };
      return validateClaimsBinding(binding) ? binding : null;
    },

    async getToken(): Promise<string> {
      const result = await acquireToken(SCOPES.FLOW);
      return result.accessToken;
    },

    async tryGetTokenForResourceSilent(resource: string): Promise<string | null> {
      try {
        validateHttpsUrl(resource, "Token resource");
        const url = new URL(resource);
        if (!isAllowedTokenDomain(url.hostname)) return null;
        if (!currentAccount) return null;
        const scopes = [`${resource}/.default`];
        const result = await pca.acquireTokenSilent({
          scopes,
          account: currentAccount,
          authority: tenantAuthorityFor(currentAccount.tenantId, authority),
        });
        return result.accessToken;
      } catch {
        return null;
      }
    },

    async getTokenForResource(resource: string): Promise<string> {
      validateHttpsUrl(resource, "Token resource");
      const url = new URL(resource);
      if (!isAllowedTokenDomain(url.hostname)) {
        throw new AuthenticationError(`Token resource not in allowlist: ${url.hostname}`);
      }
      const scopes = [`${resource}/.default`] as const;
      const result = await acquireToken(scopes);
      return result.accessToken;
    },

    async getTokenForResourceWithClaimsSilent(resource: string, claims: string): Promise<string> {
      if (
        resource !== RESOURCES.POWER_PLATFORM_API ||
        validatePowerPlatformClaims(claims) === null
      ) {
        throw new AuthenticationError(
          "Power Platform claims authentication refused an unsupported resource or challenge.",
        );
      }
      const account = currentAccount;
      if (!account) {
        throw new AuthenticationError(
          "Power Platform claims authentication requires the existing selected account; no sign-in was started.",
        );
      }
      const key = `${account.homeAccountId}|${account.tenantId}|${createHash("sha256").update(claims).digest("hex")}`;
      const pending = pendingClaimsRequests.get(key);
      if (pending) return pending;
      const acquisition = (async (): Promise<string> => {
        claimsAcquisitionsInFlight++;
        try {
          const result = await pca.acquireTokenSilent({
            account,
            scopes: [`${RESOURCES.POWER_PLATFORM_API}/.default`],
            authority: tenantAuthorityFor(account.tenantId, authority),
            forceRefresh: true,
            claims,
          });
          if (
            !result?.accessToken ||
            currentAccount !== account ||
            result.account?.homeAccountId !== account.homeAccountId ||
            result.account?.localAccountId !== account.localAccountId ||
            result.account?.tenantId !== account.tenantId ||
            result.account?.environment !== account.environment
          ) {
            throw new AuthenticationError(
              "Claims acquisition did not preserve the selected account.",
            );
          }
          return result.accessToken;
        } catch (error) {
          throw new AuthenticationError(powerPlatformClaimsFailureMessage(error));
        } finally {
          claimsAcquisitionsInFlight--;
        }
      })();
      pendingClaimsRequests.set(key, acquisition);
      try {
        return await acquisition;
      } finally {
        pendingClaimsRequests.delete(key);
      }
    },

    async logout(): Promise<void> {
      if (currentAccount) {
        const cache = pca.getTokenCache();
        // A sign-out legitimately persists an account-less cache — permit it
        // past the empty-write guard.
        permitEmptyTokenCacheWrite();
        await cache.removeAccount(currentAccount);
        currentAccount = null;
      }
      // Also clear our persisted account cache
      clearAccountCache();
    },

    getCurrentAccount(): AccountInfo | null {
      return currentAccount;
    },

    async hasAccount(): Promise<boolean> {
      const allAccounts = await pca.getAllAccounts();
      return allAccounts.length > 0;
    },
  };

  return provider;
}

/**
 * Create auth provider from config
 * @param silentOnly - If true, only attempt silent auth (for MCP server mode)
 */
export async function createAuthProviderFromConfig(
  clientId: string,
  tenantId?: string | null,
  cachePath?: string,
  silentOnly = false,
): Promise<AuthProvider> {
  const config: WamAuthConfig = {
    clientId,
    tenantId: tenantId ?? null,
  };
  if (cachePath !== undefined) {
    config.cachePath = cachePath;
  }
  return createWamAuthProvider(config, silentOnly);
}
